import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  fetchLibraryFiles,
  fetchLibraryLimits,
  fetchLibrarySuggestions,
  linkLibraryFiles,
  parseLibraryPopulation,
  type LibraryListParams,
  type LinkItem,
  type SuggestionParams,
} from '../../request/evidence-library/evidenceLibraryRequests.ts';
import { getApiErrorMessage } from '../../request/getApiErrorMessage.ts';
import { isModuleAccessDeniedError } from '../../request/moduleAccessDenied.ts';
import { evidenceLibraryQueryKeys } from '../../queries/evidence-library/keys.ts';

/**
 * React Query hooks for the Evidence Library. Library endpoints answer errors as { error }, which the axios
 * interceptor does not toast, so mutations here show the one toast; reads leave the error to the screen.
 */

export function toastLibraryError(error: unknown, fallback: string): void {
  if (!isModuleAccessDeniedError(error)) toast.error(getApiErrorMessage(error, fallback));
}

export function useLibraryLimits() {
  return useQuery({
    queryKey: evidenceLibraryQueryKeys.limits(),
    queryFn: fetchLibraryLimits,
    staleTime: 10 * 60_000,
  });
}

export function useLibraryFiles(params: LibraryListParams, enabled = true) {
  return useQuery({
    queryKey: evidenceLibraryQueryKeys.list(params),
    queryFn: () => fetchLibraryFiles(params),
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useLibrarySuggestions(params: SuggestionParams | null) {
  return useQuery({
    queryKey: params ? evidenceLibraryQueryKeys.suggestions(params) : [...evidenceLibraryQueryKeys.all, 'suggestions', 'none'],
    queryFn: () => fetchLibrarySuggestions(params as SuggestionParams),
    enabled: Boolean(params && (params.sox_control_id || params.test_run_id)),
    staleTime: 30_000,
  });
}

export function useLinkLibraryFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { sox_control_id: string; test_run_id?: string; items: LinkItem[] }) => linkLibraryFiles(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: evidenceLibraryQueryKeys.all }),
    onError: (e) => toastLibraryError(e, 'Could not link the files'),
  });
}

export function useParseLibraryPopulation() {
  return useMutation({
    mutationFn: ({ fileId, identifierColumn }: { fileId: number; identifierColumn?: string | null }) =>
      parseLibraryPopulation(fileId, identifierColumn),
  });
}

/** Call after any upload into the library so lists and suggestions refetch. */
export function useInvalidateLibrary() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: evidenceLibraryQueryKeys.all });
}
